/**
 * Unified Notification System
 * Based on the elegant locked outfit tooltip design
 * Replaces all existing toast/alert/notification systems
 */

/**
 * @typedef {Object} NotificationOptions
 * @property {string} [message] - The notification message
 * @property {string} [type] - Notification type (info, success, error, warning, locked)
 * @property {string} [hint] - Optional hint text
 * @property {string} [icon] - Icon name (without fa- prefix)
 * @property {number} [duration] - Duration in milliseconds (0 = no auto-close)
 * @property {string} [position] - Position on screen (top-right, top-center, etc.)
 * @property {HTMLElement} [relativeTo] - Position relative to this element
 * @property {boolean} [closable] - Show close button
 * @property {Function} [onClose] - Callback when closed
 * @property {Function} [onClick] - Callback when clicked
 * @property {boolean} [replaceExisting] - Replace existing notifications of same type
 */

export class UnifiedNotificationSystem {
  constructor() {
    this.notifications = new Set();
  }

  /**
   * Show a notification with the unified style
   * @param {Partial<NotificationOptions>} options - Notification options (all properties optional)
   */
  show(options = {}) {
    const {
      message = '',
      type = 'info',
      hint = '',
      icon = this.getDefaultIcon(type),
      duration = 3000,
      position = 'top-right',
      relativeTo = null,
      closable = false,
      dismissOnOutsideClick = true,
      onClose = null,
      onClick = null
    } = options;

    // Remove any existing notification of the same type if specified
    if (options.replaceExisting) {
      this.removeByType(type);
    }

    // Create notification element
    const notification = this.createNotification({
      message,
      type,
      hint,
      icon,
      closable,
      onClose,
      onClick
    });

    // Position the notification
    this.positionNotification(notification, position, relativeTo);

    // Add to DOM and tracking
    document.body.appendChild(notification);
    this.notifications.add(notification);

    // Animate in
    requestAnimationFrame(() => {
      notification.classList.add('unified-notification-show');
    });

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        this.remove(notification);
      }, duration);
    }

    // Click outside to close (only if closable and no auto-duration)
    if (closable && duration === 0 && dismissOnOutsideClick) {
      setTimeout(() => {
        const removeOnClick = (e) => {
          if (!notification.contains(e.target)) {
            this.remove(notification);
            document.removeEventListener('click', removeOnClick);
          }
        };
        document.addEventListener('click', removeOnClick);
      }, 100);
    }

    return notification;
  }

  /**
   * Create the notification DOM element
   */
  createNotification({ message, type, hint, icon, closable, onClose, onClick }) {
    const notification = document.createElement('div');
    notification.className = `unified-notification unified-notification-${type}`;
    
    // Make clickable if onClick handler is provided
    if (onClick) {
      notification.classList.add('unified-notification-clickable');
      notification.style.cursor = 'pointer';
      notification.addEventListener('click', (e) => {
        // Don't trigger if clicking close button
        if (e.target.classList.contains('unified-notification-close')) return;
        onClick(e);
      });
    }
    
    const content = document.createElement('div');
    content.className = 'unified-notification-content';

    // Icon
    if (icon) {
      const iconElement = document.createElement('i');
      iconElement.className = `fas fa-${icon}`;
      content.appendChild(iconElement);
    }

    // Main message
    const messageElement = document.createElement('span');
    messageElement.className = 'unified-notification-message';
    messageElement.innerHTML = message; // Use innerHTML to support icon tags
    content.appendChild(messageElement);

    // Hint text
    if (hint) {
      const hintElement = document.createElement('div');
      hintElement.className = 'unified-notification-hint';
      hintElement.textContent = hint;
      content.appendChild(hintElement);
    }

    // Close button
    if (closable) {
      const closeButton = document.createElement('button');
      closeButton.className = 'unified-notification-close';
      closeButton.innerHTML = '&times;';
      closeButton.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent onClick from firing
        this.remove(notification);
        if (onClose) onClose();
      });
      content.appendChild(closeButton);
    }

    notification.appendChild(content);
    
    // Add swipe-to-dismiss for mobile
    this.addSwipeGesture(notification, onClose);
    
    return notification;
  }
  
  /**
   * Add swipe gesture support for mobile
   */
  addSwipeGesture(notification, onClose) {
    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let isDragging = false;
    
    const handleTouchStart = (e) => {
      // Don't interfere with close button clicks
      if (e.target.classList.contains('unified-notification-close')) return;
      
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
      notification.style.transition = 'none';
    };
    
    const handleTouchMove = (e) => {
      if (!isDragging) return;
      
      currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = currentX - startX;
      const deltaY = currentY - startY;
      
      // Only handle horizontal swipes (more horizontal than vertical)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        e.preventDefault();
        
        // Apply transform to move notification (preserve scale)
        const opacity = 1 - Math.abs(deltaX) / 300;
        notification.style.transform = `scale(1) translateX(${deltaX}px)`;
        notification.style.opacity = Math.max(0.3, opacity);
      }
    };
    
    const handleTouchEnd = (e) => {
      if (!isDragging) return;
      
      isDragging = false;
      const deltaX = currentX - startX;
      
      // If swiped more than 100px, dismiss
      if (Math.abs(deltaX) > 100) {
        // Slide out animation
        const direction = deltaX > 0 ? '150%' : '-150%';
        notification.style.transition = 'all 0.3s ease-out';
        notification.style.transform = `scale(1) translateX(${direction})`;
        notification.style.opacity = '0';
        
        // Mark as swiped to skip the default hide animation
        notification.dataset.swipedAway = 'true';
        
        setTimeout(() => {
          this.remove(notification);
          if (onClose) onClose();
        }, 300);
      } else {
        // Snap back to original position
        notification.style.transition = 'all 0.3s ease-out';
        notification.style.transform = 'scale(1) translateX(0)';
        notification.style.opacity = '1';
      }
    };
    
    notification.addEventListener('touchstart', handleTouchStart, { passive: true });
    notification.addEventListener('touchmove', handleTouchMove, { passive: false });
    notification.addEventListener('touchend', handleTouchEnd);
  }

  /**
   * Position notification based on position and relativeTo options
   */
  positionNotification(notification, position, relativeTo) {
    if (relativeTo) {
      // Position relative to specific element (like the original locked outfit tooltip)
      const rect = relativeTo.getBoundingClientRect();
      
      // Use fixed positioning for better reliability
      Object.assign(notification.style, {
        top: `${rect.top + rect.height / 2}px`,
        left: `${rect.left + rect.width / 2}px`,
        transform: 'translate(-50%, -50%)'
      });
    } else {
      // Calculate vertical offset for stacking
      const existingNotifications = Array.from(this.notifications).filter(n => 
        n !== notification && n.parentNode && !n.classList.contains('unified-notification-hide')
      );
      
      let verticalOffset = 0;
      const spacing = 10; // Space between notifications
      
      // Calculate total height of existing notifications
      existingNotifications.forEach(n => {
        const rect = n.getBoundingClientRect();
        verticalOffset += rect.height + spacing;
      });
      
      // Position based on position string
      const baseStyles = this.getPositionStyles(position);
      
      // Adjust position for stacking (top or bottom depending on base position)
      if (baseStyles.top && position.startsWith('top-')) {
        const baseTop = parseInt(baseStyles.top);
        baseStyles.top = `${baseTop + verticalOffset}px`;
      } else if (baseStyles.bottom) {
        const baseBottom = parseInt(baseStyles.bottom);
        baseStyles.bottom = `${baseBottom + verticalOffset}px`;
      }
      
      Object.assign(notification.style, {
        ...baseStyles
      });
      
      // Store position for recalculation when notifications are removed
      notification.dataset.notificationPosition = position;
    }
  }

  /**
   * Get position styles for different positions
   */
  getPositionStyles(position) {
    // Check if mobile portrait (width <= 768px and portrait orientation)
    const isMobilePortrait = window.innerWidth <= 768 && window.innerHeight > window.innerWidth;
    
    const positions = {
      'center': {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      },
      'top-right': isMobilePortrait ? {
        top: '80px',
        right: '20px',
        transform: 'translateY(0)'
      } : {
        top: '60px',
        right: '20px',
        transform: 'translateY(0)'
      },
      'top-center': isMobilePortrait ? {
        top: '80px',
        left: '50%',
        transform: 'translateX(-50%)'
      } : {
        top: '60px',
        left: '50%',
        transform: 'translateX(-50%)'
      },
      'bottom-center': {
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)'
      },
      'top-left': isMobilePortrait ? {
        top: '80px',
        left: '20px',
        transform: 'translateY(0)'
      } : {
        top: '60px',
        left: '20px',
        transform: 'translateY(0)'
      }
    };

    return positions[position] || positions['top-right'];
  }

  /**
   * Get default icon for notification type
   */
  getDefaultIcon(type) {
    const icons = {
      'success': 'check-circle',
      'error': 'exclamation-triangle',
      'warning': 'exclamation-circle',
      'info': 'info-circle',
      'locked': 'lock'
    };
    return icons[type] || 'info-circle';
  }

  /**
   * Remove a specific notification
   */
  remove(notification) {
    if (!notification || !notification.parentNode) return;

    // If notification was swiped away, remove immediately without animation
    if (notification.dataset.swipedAway === 'true') {
      if (notification.parentNode) {
        notification.remove();
        this.notifications.delete(notification);
        
        // Reposition remaining notifications to fill the gap
        this.repositionNotifications();
      }
      return;
    }

    // Normal removal with animation
    notification.classList.remove('unified-notification-show');
    notification.classList.add('unified-notification-hide');
    
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
        this.notifications.delete(notification);
        
        // Reposition remaining notifications to fill the gap
        this.repositionNotifications();
      }
    }, 200);
  }
  
  /**
   * Reposition all notifications after one is removed
   */
  repositionNotifications() {
    const notificationsByPosition = new Map();
    
    // Group notifications by position
    Array.from(this.notifications).forEach(n => {
      if (n.parentNode && !n.classList.contains('unified-notification-hide')) {
        const position = n.dataset.notificationPosition;
        if (position) {
          if (!notificationsByPosition.has(position)) {
            notificationsByPosition.set(position, []);
          }
          notificationsByPosition.get(position).push(n);
        }
      }
    });
    
    // Reposition each group
    notificationsByPosition.forEach((notifications, position) => {
      let verticalOffset = 0;
      const spacing = 10;
      const baseStyles = this.getPositionStyles(position);
      
      notifications.forEach(n => {
        if (baseStyles.top && position.startsWith('top-')) {
          const baseTop = parseInt(baseStyles.top) || 20;
          n.style.top = `${baseTop + verticalOffset}px`;
          const rect = n.getBoundingClientRect();
          verticalOffset += rect.height + spacing;
        } else if (baseStyles.bottom) {
          const baseBottom = parseInt(baseStyles.bottom) || 20;
          n.style.bottom = `${baseBottom + verticalOffset}px`;
          const rect = n.getBoundingClientRect();
          verticalOffset += rect.height + spacing;
        }
      });
    });
  }

  /**
   * Remove notifications by type
   */
  removeByType(type) {
    const typeNotifications = document.querySelectorAll(`.unified-notification-${type}`);
    typeNotifications.forEach(notification => this.remove(notification));
  }

  /**
   * Remove all notifications
   */
  removeAll() {
    this.notifications.forEach(notification => this.remove(notification));
  }

  // Convenience methods for different types
  success(message, options = {}) {
    return this.show({ ...options, message, type: 'success' });
  }

  error(message, options = {}) {
    return this.show({ ...options, message, type: 'error' });
  }

  warning(message, options = {}) {
    return this.show({ ...options, message, type: 'warning' });
  }

  info(message, options = {}) {
    return this.show({ ...options, message, type: 'info' });
  }

  locked(message, options = {}) {
    return this.show({ 
      ...options, 
      message, 
      type: 'locked',
      hint: options.hint || 'Complete more actions to unlock'
    });
  }

  /**
   * Display a confirmation dialog styled like notifications.
   * Returns a Promise resolved with true (Yes) or false (No).
   * @param {string} message - Confirmation message
   * @param {Object} options - Additional options: yesText, noText, type, position, relativeTo, icon
   */
  confirm(message, options = {}) {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (accepted) => {
        if (settled) return;
        settled = true;
        this.remove(notification);
        resolve(accepted);
      };
      const notification = this.show({
        ...options,
        message,
        type: options.type || 'warning',
        duration: 0,
        closable: options.closable === true,
        dismissOnOutsideClick: false,
        onClose: () => finish(false)
      });

      if (options.zIndex != null) {
        notification.style.zIndex = String(options.zIndex);
      }

      const content = notification.querySelector('.unified-notification-content');

      const buttonsWrapper = document.createElement('div');
      buttonsWrapper.className = 'unified-notification-buttons';

      const yesBtn = document.createElement('button');
      yesBtn.className = 'unified-notification-btn yes';
      yesBtn.textContent = options.yesText || 'Yes';

      const noBtn = document.createElement('button');
      noBtn.className = 'unified-notification-btn no';
      noBtn.textContent = options.noText || 'No';

      yesBtn.addEventListener('click', () => finish(true));

      noBtn.addEventListener('click', () => finish(false));

      buttonsWrapper.appendChild(yesBtn);
      buttonsWrapper.appendChild(noBtn);
      content.appendChild(buttonsWrapper);
    });
  }
}

// Create global instance
const notificationSystem = new UnifiedNotificationSystem();

// Export both the class and global instance
export default notificationSystem;
export { notificationSystem };

// Convenience functions for backward compatibility
export const showNotification = (message, type = 'info', options = {}) => 
  notificationSystem.show({ message, type, ...options });

export const showSuccess = (message, options = {}) => 
  notificationSystem.success(message, options);

export const showError = (message, options = {}) => 
  notificationSystem.error(message, options);

export const showWarning = (message, options = {}) => 
  notificationSystem.warning(message, options);

export const showInfo = (message, options = {}) => 
  notificationSystem.info(message, options);

export const showLocked = (message, options = {}) => 
  notificationSystem.locked(message, options);

// Confirmation helper
export const showConfirm = (message, options = {}) =>
  notificationSystem.confirm(message, options);

// Toast-compatible functions
export const showToast = showNotification;
export const showSuccessToast = showSuccess;
export const showErrorToast = showError;
export const showWarningToast = showWarning;
export const showInfoToast = showInfo;
